import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import * as DateFns from "date-fns";
import React, { CSSProperties, useRef } from "react";
import { Modifier } from "react-day-picker";
import DayPickerInput from "react-day-picker/DayPickerInput";
import "react-day-picker/lib/style.css";
import { ClassValue, iterToArray } from "Tools";
import { ControlWrapper } from "./ControlWrapper";
import { SimpleFieldProps, useSimpleFieldProps } from "./FormTools";
import { ErrorMessage, YupDefined } from "./Tools";

export const dateFormat = "M/d/yyyy";
export const incorrectDateFormatError = `Please enter a date in the format ${dateFormat}.`;

export const defaultEarliestDate = new Date(1990, 0, 1);
export const defaultLatestDate = new Date(2500, 0, 1);

export function DateInput(props: {
  field: SimpleFieldProps<Date | null>;
  children?: React.ReactNode;
  label?: string;
  isDisabled?: boolean;
  earliestDate?: Date;
  latestDate?: Date;
  defaultMonth?: Date;
  className?: ClassValue;
  style?: CSSProperties;
  disabledDays?: Modifier[];
}) {
  const field = useSimpleFieldProps(props.field);
  const nullInvalidDate = (date: Date) => {
    if (!DateFns.isValid(date) || DateFns.getYear(date) < 1900) {
      // Note: If we define invalidDate as a value different from null (e.g. new Date(NaN))
      // then we can differentiate an error message for invalid format and empty field allowing us to create optional date fields
      // this isn't done for now because we currently assume every Date that DateInput produces is valid
      return null;
    }
    return date;
  };
  const parseDate = (e: string) => {
    const date = DateFns.parse(e, dateFormat, new Date());
    return nullInvalidDate(date);
  };
  const isOverlayVisible = useRef<boolean>(false);
  return (
    <ControlWrapper
      style={{ width: "8.5em", ...props.style }}
      className={classNames(props.className)}
      leftIconContent={<FontAwesomeIcon icon={faCalendar} />}
      errorMessage={<ErrorMessage>{field.error}</ErrorMessage>}
    >
      <DayPickerInput
        value={field.value ?? undefined}
        placeholder={dateFormat}
        format={dateFormat}
        formatDate={e => DateFns.format(e, dateFormat)}
        parseDate={e => parseDate(e) ?? undefined}
        inputProps={{
          id: field.name,
          name: field.name,
          className: "input",
          disabled: props.isDisabled,
          type: "text",
          onFocus: async (e: React.FocusEvent<HTMLInputElement>) => {
            const date = parseDate(e.target.value);
            await field.onFocus(date);
          },
          onBlur: async (e: React.FocusEvent<HTMLInputElement>) => {
            const date = parseDate(e.target.value);
            if (!isOverlayVisible.current) {
              await field.onBlur(date);
            }
          },
          autoComplete: "off",
        }}
        keepFocus={false}
        dayPickerProps={{
          month: props.defaultMonth,
          disabledDays: iterToArray(function* () {
            if (props.earliestDate) {
              yield { before: props.earliestDate };
            }
            if (props.latestDate) {
              yield { after: props.latestDate };
            }
            if (props.disabledDays) {
              yield* props.disabledDays;
            }
          }),
        }}
        onDayChange={async (e: Date | null) => {
          if (!e) return await field.onChange(null);
          const date = nullInvalidDate(DateFns.startOfDay(e));
          await field.onChange(date);
        }}
        onDayPickerShow={() => (isOverlayVisible.current = true)}
        onDayPickerHide={async () => {
          isOverlayVisible.current = false;
          await field.onBlur(field.value);
        }}
      />
    </ControlWrapper>
  );
}

DateInput.getDefaultValidator = () =>
  YupDefined.date()
    .nullable()
    .required(incorrectDateFormatError)
    .min(defaultEarliestDate, minDateMessage)
    .max(defaultLatestDate, maxDateMessage);

export function maxDateMessage(input: { max: Date | string; label?: string }) {
  return `Please enter a date on or before ${DateFns.format(
    new Date(input.max),
    dateFormat,
  )}`;
}

export function minDateMessage(input: { min: Date | string; label?: string }) {
  return `The earliest available date is ${DateFns.format(
    new Date(input.min),
    dateFormat,
  )}`;
}
